import { useState, useEffect, useCallback } from "react";
import { adminApi, type AdminActivityRow } from "@/lib/superadmin";
import {
  PageHeader,
  Card,
  ErrorBanner,
  EmptyState,
  Skeleton,
  GhostButton,
} from "../components/ui";

function fmtTime(at?: string): string {
  if (!at) return "—";
  const d = new Date(at);
  if (isNaN(d.getTime())) return at;
  return d.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function SystemPage() {
  const [rows, setRows] = useState<AdminActivityRow[]>([]);
  const [typeFilter, setTypeFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await adminApi.system.audit({
        limit: 100,
        type: typeFilter || undefined,
      });
      setRows(res.rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load audit log.");
    } finally {
      setLoading(false);
    }
  }, [typeFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-[1400px] mx-auto">
      <PageHeader
        title="Security & Audit Log"
        subtitle="Platform-wide admin actions and security-relevant events"
        actions={
          <>
            <div className="relative">
              <i className="ri-filter-3-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                placeholder="Filter by type…"
                className="pl-9 pr-3 py-2 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 w-44 transition-all"
              />
            </div>
            <GhostButton onClick={() => void load()}>
              <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`}></i>
              Refresh
            </GhostButton>
          </>
        }
      />

      {error && <ErrorBanner message={error} onRetry={() => void load()} />}

      {loading ? (
        <Card className="space-y-3">
          {[0, 1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </Card>
      ) : (
        <Card padded={false} className="overflow-hidden">
          {rows.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-foreground-500 border-b border-background-200 bg-background-100/50">
                    <th className="px-4 py-3 font-medium">When</th>
                    <th className="px-4 py-3 font-medium">Type</th>
                    <th className="px-4 py-3 font-medium">Detail</th>
                    <th className="px-4 py-3 font-medium">Restaurant</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-100">
                  {rows.map((r, i) => (
                    <tr key={i} className="hover:bg-background-100/40">
                      <td className="px-4 py-3 text-foreground-500 whitespace-nowrap">{fmtTime(r.at)}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-primary-50 text-primary-700">
                          {r.type ?? "event"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-foreground-700">{r.detail ?? "—"}</td>
                      <td className="px-4 py-3 font-mono text-xs text-foreground-500">{r.restaurantId ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState
              icon="ri-shield-check-line"
              title="No audit events recorded"
              hint="Admin actions and security events will be logged here."
            />
          )}
        </Card>
      )}
    </div>
  );
}
